import { useEffect, useState } from 'react';
import { AlertTriangle, ArrowRight, Info, Loader2, XCircle } from 'lucide-react';
import { useAuth } from '@/features/auth';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { listNotifications, type NotificationRecord } from '@/features/planning/api';
import type { Language } from '@/types';

interface Props {
  language: Language;
  onOpenAll?: () => void;
  max?: number;
}

const severityIcon = {
  info: Info,
  warning: AlertTriangle,
  error: XCircle,
};

export function NotificationsWidget({ language, onOpenAll, max = 4 }: Props) {
  const { session } = useAuth();
  const token = session?.access_token ?? null;
  const [items, setItems] = useState<NotificationRecord[]>([]);
  const [counts, setCounts] = useState<{ unseen: number; warning: number; error: number } | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    const load = async () => {
      try {
        const res = await listNotifications(token, false);
        if (cancelled) return;
        setItems(res.notifications.slice(0, max));
        setCounts(res.counts);
      } catch {
        // silent
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [token, max]);

  const title = language === 'sv' ? 'Notifieringar' : 'Notifications';
  const viewAll = language === 'sv' ? 'Se alla' : 'View all';
  const empty = language === 'sv' ? 'Inga aktiva varningar.' : 'No active warnings.';

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            {title}
            {counts && counts.unseen > 0 && (
              <Badge variant="warning">{counts.unseen}</Badge>
            )}
          </CardTitle>
          {onOpenAll && (
            <Button variant="ghost" size="sm" onClick={onOpenAll}>
              {viewAll}
              <ArrowRight className="ml-1 h-3 w-3" />
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {loading ? (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : items.length === 0 ? (
          <p className="text-sm text-muted-foreground py-2">{empty}</p>
        ) : (
          <ul className="space-y-2">
            {items.map((n) => {
              const Icon = severityIcon[n.severity];
              return (
                <li key={n.id} className="flex items-start gap-2 text-sm">
                  <Icon className="h-4 w-4 shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{n.title}</div>
                    {n.body && <div className="text-xs text-muted-foreground truncate">{n.body}</div>}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
